"use client";

import { AnimatePresence, motion } from "motion/react";
import { Popover as PopoverPrimitive } from "radix-ui";
import { type ReactNode, useState } from "react";
import { FormattedMessage } from "react-intl";
import { Button, cn } from "../..";

interface PopoverProps extends PopoverPrimitive.PopoverProps {
	content: ReactNode;
	side?: PopoverPrimitive.PopoverContentProps["side"];
	sideOffset?: PopoverPrimitive.PopoverContentProps["sideOffset"];
	color?: "shades-light" | "dark" | "primary-dark";
	close?: boolean;
}

export function Popover(props: PopoverProps) {
	const {
		side = "bottom",
		sideOffset = 1,
		content,
		color = "dark",
		close = false,
		children,
		...remainingProps
	} = props;

	const [open, onOpenChange] = useState(false);

	return (
		<PopoverPrimitive.Root
			open={remainingProps.open !== undefined ? remainingProps.open : open}
			onOpenChange={
				remainingProps.onOpenChange !== undefined ? remainingProps.onOpenChange : onOpenChange
			}
			{...remainingProps}
		>
			<PopoverPrimitive.Trigger asChild>{children}</PopoverPrimitive.Trigger>
			<AnimatePresence>
				{(remainingProps.open !== undefined ? remainingProps.open : open) && (
					<PopoverPrimitive.Portal forceMount>
						<PopoverPrimitive.Content
							forceMount
							side={side}
							sideOffset={sideOffset}
							className="focus:outline-none"
						>
							<motion.div
								className={cn(
									"max-w-[min(calc(100vw-2rem),20rem)] hyphens-auto break-words rounded px-2 py-1 text-sm shadow",
									{
										"border-shades-light bg-shades-light text-dark": color === "shades-light",
										"bg-dark text-white": color === "dark",
										"bg-primary-dark text-white": color === "primary-dark",
									},
								)}
								transition={{ duration: 0.15 }}
								initial={{ opacity: 0, scale: 0.9 }}
								animate={{ opacity: 1, scale: 1 }}
								exit={{ opacity: 0, scale: 0.9 }}
							>
								<PopoverPrimitive.Arrow
									className={cn("fill-current", {
										"text-shades-light": color === "shades-light",
										"text-dark": color === "dark",
										"text-primary-dark": color === "primary-dark",
									})}
								/>

								<div className="space-x-1">
									{close && (
										<PopoverPrimitive.Close asChild>
											<Button color="white" size="small">
												<FormattedMessage defaultMessage="Oeps, nee" />
											</Button>
										</PopoverPrimitive.Close>
									)}

									{typeof content === "string" ? (
										<div
											className={cn("text-sm", {
												"text-dark": color === "shades-light",
												"text-white": color === "dark" || color === "primary-dark",
											})}
											dangerouslySetInnerHTML={{ __html: content }}
										/>
									) : (
										content
									)}
								</div>
							</motion.div>
						</PopoverPrimitive.Content>
					</PopoverPrimitive.Portal>
				)}
			</AnimatePresence>
		</PopoverPrimitive.Root>
	);
}
